import { P2P_MESSAGE_TYPES, createP2PMessage } from '../services/MultiplayerMessageTypes.js';
import { broadcastMessage, sendMessageToPeer, getMyPeerId } from '../services/MultiplayerService.js'; // Assuming sendMessageToPeer can be used for sendToHost for now

export const EVENT_TYPES = {
    // Core Lifecycle & Setup
    BATTLE_INITIALIZE_REQUEST: 'BATTLE_INITIALIZE_REQUEST', // payload: { config: { playerSnordeluxeName, opponentSnordeluxeName, mode, etc. } }
    BATTLE_INITIALIZED: 'BATTLE_INITIALIZED',       // payload: { initialState }
    GAME_LOOP_START_REQUEST: 'GAME_LOOP_START_REQUEST',
    GAME_LOOP_STOP_REQUEST: 'GAME_LOOP_STOP_REQUEST',
    GAME_TICK: 'GAME_TICK',                         // payload: { deltaTime, timestamp }

    // Character Events
    CHARACTER_SPAWN_REQUESTED: 'CHARACTER_SPAWN_REQUESTED', // payload: { id (optional), type ('player'|'enemy'), snordeluxeName, initialX, initialY, ... }
    CHARACTER_SPAWNED: 'CHARACTER_SPAWNED',                 // payload: { characterData }
    CHARACTER_DESPAWN_REQUESTED: 'CHARACTER_DESPAWN_REQUESTED',// payload: { id }
    CHARACTER_DESPAWNED: 'CHARACTER_DESPAWNED',           // payload: { id }
    CHARACTER_POSITION_UPDATED: 'CHARACTER_POSITION_UPDATED', // payload: { id, x, y, velocityX, velocityY, isGrounded, direction }
    CHARACTER_HEALTH_UPDATED: 'CHARACTER_HEALTH_UPDATED',   // payload: { id, newHealth, maxHealth, damageTaken?, healingReceived?, attackerId? }
    CHARACTER_DIED: 'CHARACTER_DIED',                       // payload: { id, killerId? }
    CHARACTER_RESPAWN: 'CHARACTER_RESPAWN',                 // payload: { id, x, y, livesRemaining }
    CHARACTER_EFFECT_APPLIED: 'CHARACTER_EFFECT_APPLIED',   // payload: { id, effectType, duration? }
    CHARACTER_EFFECT_REMOVED: 'CHARACTER_EFFECT_REMOVED',   // payload: { id, effectType }

    // Input & Intent
    PLAYER_INPUT_RECEIVED: 'PLAYER_INPUT_RECEIVED',         // payload: { playerId, inputType ('MOVE_LEFT_START', 'JUMP' etc.) }
    CHARACTER_MOVEMENT_INTENT_UPDATED: 'CHARACTER_MOVEMENT_INTENT_UPDATED', // payload: { id, intent: { left, right, jump } }
    CHARACTER_ATTACK_INTENT: 'CHARACTER_ATTACK_INTENT',     // payload: { id, attackIndex } (or attackName)

    // Physics & State Updates
    CHARACTER_PRE_PHYSICS_UPDATE: 'CHARACTER_PRE_PHYSICS_UPDATE', // payload: { id, characterData, deltaTime } (for systems to modify before physics)

    // Attack Lifecycle
    ATTACK_INITIATED: 'ATTACK_INITIATED',           // payload: { ownerId, attackName, attackId (unique instance), startX, startY, directionVector, attackData }
    ATTACK_PROPAGATED: 'ATTACK_PROPAGATED',          // payload: { attackId, x, y, width, height, velocityX, ... } (snapshot of the attack entity)
    ATTACK_HIT_CHARACTER: 'ATTACK_HIT_CHARACTER',   // payload: { attackId, attackerId, victimId, damageAmount }
    ATTACK_EXPIRED: 'ATTACK_EXPIRED',               // payload: { attackId }
    ATTACK_COOLDOWN_STARTED: 'ATTACK_COOLDOWN_STARTED', // payload: { ownerId, attackName, duration }
    ATTACK_COOLDOWN_FINISHED: 'ATTACK_COOLDOWN_FINISHED',// payload: { ownerId, attackName }

    // Per-tier ability usage. Triggered by player pressing Q or by AI ticker.
    // payload: { casterId }
    ABILITY_USE_REQUESTED: 'ABILITY_USE_REQUESTED',

    // Powerup Lifecycle
    POWERUP_SPAWN_REQUESTED: 'POWERUP_SPAWN_REQUESTED', // payload: { type, x?, y? } (position might be determined by a spawner system)
    POWERUP_SPAWNED: 'POWERUP_SPAWNED',                 // payload: { powerupInstanceId, type, x, y, settings }
    POWERUP_COLLECT_REQUESTED: 'POWERUP_COLLECT_REQUESTED',// payload: { powerupInstanceId, collectorId }
    POWERUP_COLLECTED: 'POWERUP_COLLECTED',             // payload: { powerupInstanceId, collectorId, powerupType }
    POWERUP_EXPIRED: 'POWERUP_EXPIRED',                 // payload: { powerupInstanceId }

    // World & Environment
    PLATFORM_CREATED: 'PLATFORM_CREATED',           // payload: { platformData }
    THEME_CHANGED: 'THEME_CHANGED',                 // payload: { themeName, themeConfig }
    ENVIRONMENT_HAZARD_TRIGGERED: 'ENVIRONMENT_HAZARD_TRIGGERED', // payload: { hazardType, characterId? }

    // UI & Game Flow
    WAVE_STARTED: 'WAVE_STARTED',                   // payload: { waveNumber, themeName }
    WAVE_COMPLETED: 'WAVE_COMPLETED',               // payload: { waveNumber }
    WAVE_SPAWN_NEXT: 'WAVE_SPAWN_NEXT',             // payload: { waveNumber, enemyCount }
    BATTLE_VICTORY: 'BATTLE_VICTORY',               // payload: { finalWave?, rewards? }
    BATTLE_DEFEAT: 'BATTLE_DEFEAT',                 // payload: { finalWave? }
    SCREEN_SHAKE_REQUESTED: 'SCREEN_SHAKE_REQUESTED', // payload: { intensity, duration }
    SOUND_PLAY_REQUESTED: 'SOUND_PLAY_REQUESTED',     // payload: { soundName, volume? }
    UI_UPDATE_REQUESTED: 'UI_UPDATE_REQUESTED',       // payload: { uiElement, data } (e.g. health bar, cooldown icon)
    EFFECT_SPAWN_REQUESTED: 'EFFECT_SPAWN_REQUESTED', // payload: { effectType, x, y, targetId?, isExtreme?, otherData? }
    EFFECT_STATE_UPDATED: 'EFFECT_STATE_UPDATED',     // payload: { effectId, newData }
    EFFECT_EXPIRED: 'EFFECT_EXPIRED',                 // payload: { effectId }
    
    // Network Synchronization
    POSITION_SYNC_SNAPSHOT: 'POSITION_SYNC_SNAPSHOT', // payload: { positions: { [id]: { x, y, velocityX, velocityY, direction, isGrounded, health } }, timestamp }

    // Camera events
    CAMERA_POSITION_UPDATED: 'CAMERA_POSITION_UPDATED'
};

let gameState = {
    nextEntityId: 1,
    characters: {}, // keyed by id: { id, type, snordeluxeName, x, y, velocityX, velocityY, health, maxHealth, direction, isGrounded, attacks, currentAttack, animationState, movementIntent: {left,right,jump}, etc. }
    activeAttacks: {}, // keyed by attackId: { id, ownerId, type, x, y, velocityX, lifetime, damage, width, height, etc. }
    activePowerups: {}, // keyed by powerupInstanceId: { id, type, x, y, settings, bobOffset, createdAt, ... }
    activeVisualEffects: {}, // New: To store active visual effects like explosions, hits
    platforms: [], // array of platform objects: { id, x, y, width, height, type }
    battleStatus: {
        mode: null, // 'normal', 'extreme'
        waveNumber: 1, // Start at wave 1 instead of 0
        enemiesDefeated: 0, // Track total enemies defeated
        playerKills: 0, // Kills credited to the local player this battle (drives mana-drop bonus)
        currentWaveEnemies: 1, // Number of enemies in current wave
        theme: null, // current theme name
        themeConfig: null,
        isPaused: false,
        battleResult: null, // 'win', 'loss', 'inprogress'
        exitReason: null, // 'manual_exit', 'player_died', 'left_early'
        timestamp: 0, // current game time
        cameraXOffset: 0, // NEW: Camera X position for horizontal panning
        cameraYOffset: 0,
        cameraZoom: 1,
        currentArenaYOffset: 0,
        canvasWidth: 0, // Should be set on battle init (viewport size)
        canvasHeight: 0, // Should be set on battle init (viewport size)
        virtualArenaWidth: 2400, // NEW: Virtual arena width (3x typical screen width)
        virtualArenaHeight: 1600, // NEW: Virtual arena height (larger than typical screen)
        cameraFollowSmoothness: 0.08, // NEW: Camera smoothing factor (0.1 = fast, 0.05 = slow)
        cameraZoomSmoothness: 0.06,
        currentGravity: 0.5,
        defeatedEnemyName: null, // Store enemy name for victory screen display
        defeatedPlayerName: null, // Store player name for defeat screen display
        // Host-selectable gameplay modifiers. Toggled in the lobby
        // browser; serialized to clients via SESSION_JOIN_RESPONSE
        // (which copies battleStatus wholesale). Effects:
        //   lifesteal   — +1 livesRemaining on each kill
        //   fatMode     — +12% size on each kill, capped (see FAT_GROW_PER_KILL/FAT_MAX_SCALE)
        //   glassCannon — 2× damage dealt AND taken (applied in AttackSystem)
        //   lowGravity  — gravity set to 0.5× the theme default
        gameModes: { lifesteal: false, fatMode: false, glassCannon: false, lowGravity: false },
        hostMigrationRoster: []
    },
    multiplayer: { // Added for multiplayer state
        isMultiplayer: false,
        isHost: false,
        hostPeerId: null,
        myPeerId: null // Initialized to null
    },
    // Potentially other game-wide states
};

const eventQueue = [];
const subscriptions = {};
let coreGameLoopId = null;
let lastTickTimestamp = 0;

// Define a list of events that should be broadcast by the host
const HOST_BROADCAST_EVENTS = [
    EVENT_TYPES.BATTLE_INITIALIZED,
    EVENT_TYPES.CHARACTER_SPAWNED,
    EVENT_TYPES.CHARACTER_DESPAWN_REQUESTED,
    EVENT_TYPES.CHARACTER_DESPAWNED,
    EVENT_TYPES.CHARACTER_POSITION_UPDATED, // Re-enabled for client position updates
    EVENT_TYPES.CHARACTER_HEALTH_UPDATED,
    EVENT_TYPES.CHARACTER_DIED,
    EVENT_TYPES.CHARACTER_RESPAWN,
    EVENT_TYPES.CHARACTER_EFFECT_APPLIED,
    EVENT_TYPES.CHARACTER_EFFECT_REMOVED,
    EVENT_TYPES.ATTACK_INITIATED,
    EVENT_TYPES.ATTACK_PROPAGATED, // Potentially, if clients don't simulate this fully
    EVENT_TYPES.ATTACK_HIT_CHARACTER,
    EVENT_TYPES.ATTACK_EXPIRED,
    EVENT_TYPES.ATTACK_COOLDOWN_STARTED,
    EVENT_TYPES.ATTACK_COOLDOWN_FINISHED,
    EVENT_TYPES.ABILITY_USE_REQUESTED,
    EVENT_TYPES.POWERUP_SPAWNED,
    EVENT_TYPES.POWERUP_COLLECTED,
    EVENT_TYPES.POWERUP_EXPIRED,
    EVENT_TYPES.PLATFORM_CREATED, // If platforms can change dynamically
    EVENT_TYPES.THEME_CHANGED,
    EVENT_TYPES.ENVIRONMENT_HAZARD_TRIGGERED,
    EVENT_TYPES.WAVE_STARTED,
    EVENT_TYPES.WAVE_COMPLETED,
    EVENT_TYPES.BATTLE_VICTORY,
    EVENT_TYPES.BATTLE_DEFEAT,
    EVENT_TYPES.SCREEN_SHAKE_REQUESTED, // Visual effects can be good to sync
    EVENT_TYPES.SOUND_PLAY_REQUESTED,   // Sounds can be good too, though less critical for state
    EVENT_TYPES.EFFECT_SPAWN_REQUESTED,
    EVENT_TYPES.EFFECT_EXPIRED,
    EVENT_TYPES.POSITION_SYNC_SNAPSHOT, // Add position sync to broadcast list
    // Add any other events that modify shared game state or are important for all players to observe
];

export function getNextId() {
    return `e_${gameState.nextEntityId++}`;
}

export function subscribe(eventType, handler) {
    if (!subscriptions[eventType]) {
        subscriptions[eventType] = [];
    }
    if (!subscriptions[eventType].includes(handler)) {
        subscriptions[eventType].push(handler);
    }
}

export function unsubscribe(eventType, handler) {
    if (subscriptions[eventType]) {
        subscriptions[eventType] = subscriptions[eventType].filter(h => h !== handler);
    }
}

export function queueEvent(event) {
    // console.log('Queueing Event:', event.type, event.payload); // Verbose: remove in production

    const { isMultiplayer, isHost, hostPeerId, myPeerId } = gameState.multiplayer;

    if (isMultiplayer) {
        if (isHost) {
            // HOST anD EVENT ORIGINATED LOCALLY (OR IS BEING PROCESSED BY HOST FROM A CLIENT)
            if (event.fromNetwork) {
                // Event received from network (e.g. host processing its own broadcast for simulation - usually not needed with this model)
                // Or, an event that was already processed for networking. Just queue locally.
                eventQueue.push(event);
            } else {
                // Event originated on the host (e.g. host's own input, AI, physics results)
                // Queue it locally first
                eventQueue.push(event);
                
                // If it's a broadcast-worthy event, send it to all clients
                if (HOST_BROADCAST_EVENTS.includes(event.type)) {
                    const p2pMessage = createP2PMessage(P2P_MESSAGE_TYPES.GAME_EVENT_FROM_HOST, { gameEvent: event });
                    broadcastMessage(p2pMessage); // Assumes MultiplayerService.broadcastMessage is available and works
                    // console.log(`[Host] Broadcasted event: ${event.type}`, p2pMessage);
                }
            }
        } else {
            // CLIENT
            if (event.fromNetwork) {
                // Event received from host, queue it locally for processing
                eventQueue.push(event);
            } else {
                // Event originated locally on the client
                if (event.type === EVENT_TYPES.PLAYER_INPUT_RECEIVED && event.payload.playerId === myPeerId) {
                    // Client's own input, send to host instead of processing locally directly
                    const p2pMessage = createP2PMessage(P2P_MESSAGE_TYPES.PLAYER_INPUT_TO_HOST, { inputEvent: event });
                    if (hostPeerId) {
                        sendMessageToPeer(hostPeerId, p2pMessage); // Assumes MultiplayerService.sendMessageToPeer is available
                        // console.log(`[Client] Sent input to host: ${event.payload.inputType}`, p2pMessage);
                    } else {
                        console.warn('[Client] No hostPeerId to send input to.');
                    }
                    // Do NOT push this event to the local queue directly, wait for host's authority
                } else {
                    // Other local events (e.g., UI, non-state-critical sounds not covered by host broadcast)
                    // For now, let's assume most client-originated events that affect shared state should not be queued locally.
                    // This part might need refinement based on which events clients can generate that are purely local.
                    // console.log(`[Client] Locally originated event ${event.type} - considering for local queue only if non-state critical.`);
                    if (!HOST_BROADCAST_EVENTS.includes(event.type)) {
                         eventQueue.push(event); // Example: A purely visual client-side effect not tied to game state
                    }
                }
            }
        }
    } else {
        // Not multiplayer, just queue locally
        eventQueue.push(event);
    }
}

export function processQueue() {
    // Process a copy and clear original in case handlers queue more events
    const queueToProcess = [...eventQueue];
    eventQueue.length = 0; 

    // Debug log for client
    if (gameState.multiplayer.isMultiplayer && !gameState.multiplayer.isHost && queueToProcess.length > 0) {
        console.log(`[Client processQueue] Processing ${queueToProcess.length} events:`, queueToProcess.map(e => e.type));
    }

    for (const event of queueToProcess) {
        if (subscriptions[event.type]) {
            subscriptions[event.type].forEach(handler => {
                try {
                    handler(event.payload, gameState); // Pass gameState to handlers
                } catch (error) {
                    console.error(`Error in event handler for ${event.type}:`, error, 'Event:', event);
                }
            });
        }
    }
}

// Placeholder for actual game logic modules
let PhysicsSystem = { update: (gs, deltaTime) => {} };
let AISystem = { update: (gs, deltaTime) => {} };
let RenderSystem = { render: (gs, ctx) => {} }; // Assuming canvas context is managed elsewhere or passed in
let EffectsSystem = { handleEvent: (payload, gs) => {} }; // e.g. for sounds, screen shake
let UISystem = { handleEvent: (payload, gs) => {} }; // For updating HTML UI elements

export function registerPhysicsSystem(system) { PhysicsSystem = system; }
export function registerAISystem(system) { AISystem = system; }
export function registerRenderSystem(system) { RenderSystem = system; }
export function registerEffectsSystem(system) { EffectsSystem = system; }
export function registerUISystem(system) { UISystem = system; }


// --- Street-fighter hit-stop ---
// AttackSystem calls setHitStop(ms) on a successful hit; the game loop
// freezes the simulation tick (movement, physics, AI) for that duration
// while still processing events and rendering. Result: a brief, weighty
// pause that sells the impact, classic SF flavour.
let hitStopUntil = 0;
export function setHitStop(durationMs) {
    const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    const target = now + Math.max(0, durationMs);
    if (target > hitStopUntil) hitStopUntil = target;
}
export function isHitStopActive(timestamp) {
    return timestamp < hitStopUntil;
}

function coreGameLoop(timestamp) {
    if (!lastTickTimestamp) {
        lastTickTimestamp = timestamp;
    }
    const deltaTime = timestamp - lastTickTimestamp;
    lastTickTimestamp = timestamp;
    gameState.battleStatus.timestamp = timestamp;

    // 1. Process all queued events (inputs, state changes from previous frame)
    processQueue();

    // 2. Update Systems (if battle is not paused and has a result of null/inprogress)
    //    Hit-stop temporarily skips the tick to freeze the action.
    const tickAllowed = !gameState.battleStatus.isPaused
                     && !gameState.battleStatus.battleResult
                     && !isHitStopActive(timestamp);
    if (tickAllowed) {
        queueEvent({ type: EVENT_TYPES.GAME_TICK, payload: { deltaTime, timestamp }});
    }

    // Process any events generated by systems during the tick
    processQueue();

    // 3. Render the current state (RenderSystem would subscribe to a RENDER_FRAME event or be called directly)
    // For now, let's assume RenderSystem.render is called from the main game loop
    // after coreGameLoop finishes its processing for the tick.
    // Or, the render system could subscribe to an event like AFTER_STATE_UPDATE
    
    // If the battle is still ongoing
    if (!gameState.battleStatus.battleResult) {
      coreGameLoopId = requestAnimationFrame(coreGameLoop);
    } else {
      console.log("Battle ended. Core loop stopping.", gameState.battleStatus.battleResult);
    }
}

export function startGameLoopCore() {
    if (coreGameLoopId) {
        console.warn("Core game loop already running.");
        return;
    }
    console.log("Starting core game loop.");
    lastTickTimestamp = performance.now(); // Reset timestamp for new loop
    gameState.battleStatus.battleResult = null; // Ensure battle can start
    gameState.battleStatus.isPaused = false;
    installVisibilityPause();
    coreGameLoopId = requestAnimationFrame(coreGameLoop);
}

// Pause the sim + audio when the tab is backgrounded so the AI doesn't
// keep computing and music doesn't keep playing in another tab. Browsers
// already throttle rAF on hidden tabs to ~1 FPS, but isPaused short-
// circuits GAME_TICK entirely (no AI, no physics, no charge-time drift),
// and pausing the audio elements cuts CPU/battery to ~0 on a phone the
// user just locked. Installed once per session.
let visibilityPauseInstalled = false;
let audioElsPausedByVisibility = [];
function installVisibilityPause() {
    if (visibilityPauseInstalled || typeof document === 'undefined') return;
    visibilityPauseInstalled = true;
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            if (gameState && gameState.battleStatus) gameState.battleStatus.isPaused = true;
            audioElsPausedByVisibility = [];
            try {
                document.querySelectorAll('audio').forEach((a) => {
                    if (!a.paused) { a.pause(); audioElsPausedByVisibility.push(a); }
                });
            } catch (_) {}
        } else {
            if (gameState && gameState.battleStatus) gameState.battleStatus.isPaused = false;
            // Resume only the elements we paused, so audio the user
            // had already muted stays muted.
            try {
                audioElsPausedByVisibility.forEach((a) => { try { a.play().catch(() => {}); } catch (_) {} });
            } catch (_) {}
            audioElsPausedByVisibility = [];
        }
    });
}

export function stopGameLoopCore() {
    if (coreGameLoopId) {
        cancelAnimationFrame(coreGameLoopId);
        coreGameLoopId = null;
        console.log("Core game loop stopped.");
    }
}

export function getGameState() {
    // Return a deep copy if state is complex and direct mutation is a risk,
    // or ensure all state mutations happen via events. For now, direct reference.
    return gameState;
}

export function resetCoreGameState() {
    stopGameLoopCore(); // Ensure the game loop is stopped before resetting state
    console.log("[battle-core.js] Resetting core game state");
    gameState.nextEntityId = 1;
    gameState.characters = {};
    gameState.activeAttacks = {};
    gameState.activePowerups = {};
    gameState.activeVisualEffects = {}; 
    gameState.platforms = []; 
    gameState.battleStatus = {
        mode: null,
        waveNumber: 1,
        enemiesDefeated: 0,
        playerKills: 0,
        currentWaveEnemies: 1,
        theme: null,
        themeConfig: null,
        isPaused: false,
        battleResult: null,
        exitReason: null,
        timestamp: 0,
        cameraXOffset: 0,
        cameraYOffset: 0,
        cameraZoom: 1,
        currentArenaYOffset: 0,
        canvasWidth: typeof window !== 'undefined' ? window.innerWidth : 800, // Default on reset
        canvasHeight: typeof window !== 'undefined' ? window.innerHeight : 600, // Default on reset
        virtualArenaWidth: 2400,
        virtualArenaHeight: 1600,
        cameraFollowSmoothness: 0.08,
        cameraZoomSmoothness: 0.06,
        currentGravity: 0.5,
        defeatedEnemyName: null,
        defeatedPlayerName: null,
        gameModes: { lifesteal: false, fatMode: false, glassCannon: false, lowGravity: false },
        hostMigrationRoster: []
    };
    gameState.multiplayer = { // Reset multiplayer state too
        isMultiplayer: false,
        isHost: false,
        hostPeerId: null,
        myPeerId: null // Initialize to null, to be set explicitly for MP games
    };
    eventQueue.length = 0; 
}

// New function to configure multiplayer settings
export function setMultiplayerConfig(mpConfig) {
    if (!mpConfig) {
        console.warn("[BattleCore] setMultiplayerConfig called with no config. Setting to non-multiplayer.");
        gameState.multiplayer.isMultiplayer = false;
        gameState.multiplayer.isHost = false;
        gameState.multiplayer.myPeerId = null;
        gameState.multiplayer.hostPeerId = null;
        return;
    }

    gameState.multiplayer.isMultiplayer = !!mpConfig.isMultiplayer;
    gameState.multiplayer.isHost = !!mpConfig.isHost;
    gameState.multiplayer.myPeerId = mpConfig.myPeerId || null;
    gameState.multiplayer.hostPeerId = mpConfig.hostPeerId || null;

    // Special case: if isMultiplayer is true, but myPeerId is somehow still null, log a warning.
    // This helps catch scenarios where the multiplayer setup logic might be incomplete.
    if (gameState.multiplayer.isMultiplayer && !gameState.multiplayer.myPeerId) {
        console.warn("[BattleCore] Multiplayer mode enabled, but myPeerId is null. Inputs may not work. Provided config:", mpConfig);
    }
    
    // If it's a client, it must have a hostPeerId
    if (gameState.multiplayer.isMultiplayer && !gameState.multiplayer.isHost && !gameState.multiplayer.hostPeerId) {
        console.warn("[BattleCore] Client mode enabled, but hostPeerId is null. Communication with host will fail. Provided config:", mpConfig);
    }

    console.log("[BattleCore] Multiplayer configuration set:", JSON.stringify(gameState.multiplayer));
}

// Export processQueue so it can be called externally if needed (e.g., for initial setup)
// Already exported above when declared, so we don't need to export it again

// Initialize with default systems (can be replaced by actual systems later)
// This is more for demonstration of how systems might plug in.
// Actual system registration would happen in the main game initialization or similar.
// registerPhysicsSystem({ update: (gs, dt) => console.log("Dummy Physics Tick", dt) });
// registerAISystem({ update: (gs, dt) => console.log("Dummy AI Tick", dt) });

// Export eventQueue directly
export { eventQueue };

console.log("battle-core.js loaded");
